import React, { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

/**
 * @param {object} notification
 * @returns {string}
 */
const groupKeyFor = (notification) => {
    if (notification.thread_id) {
        return `thread:${notification.thread_id}:${notification.method || ''}`;
    }
    return `content:${notification.heading}:${notification.message}:${notification.method || ''}`;
};

/**
 * @param {object} notification
 * @returns {string|null}
 */
const destinationFrom = (notification) => {
    const dest = notification.metadata && notification.metadata.destination;
    if (dest == null || dest === '') {
        return null;
    }
    return String(dest);
};

const PaymentNotify = ({ payment }) => {
    const { t } = useTranslation();

    const grouped = useMemo(() => {
        const list = payment && payment.notifications;
        if (!list || !list.length) {
            return [];
        }

        const byKey = new Map();
        for (const notification of list) {
            const key = groupKeyFor(notification);
            let entry = byKey.get(key);
            if (!entry) {
                entry = {
                    key,
                    sample: notification,
                    destinations: [],
                    createdAts: [],
                };
                byKey.set(key, entry);
            }
            const dest = destinationFrom(notification);
            if (dest) {
                entry.destinations.push(dest);
            }
            if (notification.created_at) {
                entry.createdAts.push(notification.created_at);
            }
        }

        const rows = [];
        for (const entry of byKey.values()) {
            const uniqueDestinations = [...new Set(entry.destinations)].sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' })
            );
            const createdAt = entry.createdAts.length
                ? entry.createdAts.reduce((min, cur) =>
                      !min || moment(cur).isBefore(moment(min)) ? cur : min
                  )
                : entry.sample.created_at;
            rows.push({
                key: entry.key,
                notification: entry.sample,
                destinations: uniqueDestinations,
                createdAt,
            });
        }

        rows.sort((a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf());
        return rows;
    }, [payment]);

    if (!payment || !payment.notifications || !payment.notifications.length) {
        return <p className="text-muted">{t('payments.notify_empty')}</p>;
    }

    return grouped.map((row) => {
        const { notification } = row;
        const recipientsDisplay =
            row.destinations.length > 0
                ? row.destinations.join(', ').replace(/,(?!\s)/g, ', ')
                : t('payments.notify_recipient_unknown');
        const channelLabel =
            notification.method === 'sms'
                ? t('payments.notify_channel_sms')
                : notification.method === 'email'
                  ? t('payments.notify_channel_email')
                  : notification.method;

        return (
            <div key={row.key} className="PaymentNotify">
                <p>
                    {t('common.by')} {notification.creator && notification.creator.fullname},{' '}
                    {t('common.the')} {moment(row.createdAt).format('LLL')}.
                </p>
                <p>
                    <strong>{t('payments.notify_recipients')}:</strong> {recipientsDisplay}
                    {channelLabel ? (
                        <span className="text-muted">
                            {' '}
                            ({channelLabel})
                        </span>
                    ) : null}
                </p>
                <h5>{notification.heading}</h5>
                <div dangerouslySetInnerHTML={{ __html: notification.message }} />
                <hr
                    style={{
                        backgroundColor: '#fff',
                        height: 0.5,
                    }}
                />
            </div>
        );
    });
};

export default PaymentNotify;
